import React from 'react';
import { Box, Card, CardContent, Skeleton, Stack, Tooltip, Typography, useTheme } from '@mui/material';

import Sparkline from './Sparkline';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
import { opsColors } from '../../../../theme/opsTokens';

export interface KpiTileProps {
  label: string;
  value: string | number;
  subtitle?: string;
  /** Previous-period value for delta computation. */
  previousValue?: number | null;
  /** Trend values for sparkline. */
  trend?: number[];
  /** Color override for the sparkline. Defaults to theme primary. */
  color?: string;
  /** When true, indicate higher = better; when false, lower = better; when undefined, neutral colour. */
  higherIsBetter?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

function formatDelta(current: number, previous: number): string {
  if (previous === 0) return current === 0 ? '0%' : '∞';
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  subtitle,
  previousValue,
  trend,
  color,
  higherIsBetter = true,
  loading = false,
  icon,
}) => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  const hasComparison = typeof previousValue === 'number' && Number.isFinite(numericValue);
  const delta = hasComparison ? numericValue - (previousValue ?? 0) : 0;
  const goingUp = delta > 0;
  const noChange = delta === 0;
  const positiveChange = goingUp === higherIsBetter && !noChange;
  const deltaColor = noChange
    ? opsColors.neutral.inkSoft
    : positiveChange
      ? opsColors.semantic.success.color
      : opsColors.semantic.error.color;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon && (
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Stack>

          {loading ? (
            <Skeleton variant="text" width="60%" height={36} />
          ) : (
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={0.5} alignItems="center">
              {hasComparison && !loading && (
                <Tooltip title={`Previous: ${(previousValue ?? 0).toLocaleString()}`} arrow>
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: deltaColor }}>
                    {noChange ? (
                      <Icon size={20} name="minus" />
                    ) : goingUp ? (
                      <Icon size={20} d={ICON_FALLBACKS.ArrowUpward} />
                    ) : (
                      <Icon size={20} name="arrow-down-long" />
                    )}
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatDelta(numericValue, previousValue ?? 0)}
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
              {subtitle && !loading && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: hasComparison ? 0.5 : 0 }}>
                  {subtitle}
                </Typography>
              )}
            </Stack>

            {trend && trend.length > 1 && !loading && (
              <Sparkline values={trend} color={color} width={100} height={32} ariaLabel={`${label} trend`} />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default React.memo(KpiTile);
